import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { FileText, Package, ArrowRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

const STATUS_LABELS = { pending: "Pendiente", reviewing: "En revisión", approved: "Aprobada", rejected: "Rechazada" };
const ORDER_STATUS = { pending: "Pendiente", design: "Diseño", production: "En producción", ready: "Listo", delivered: "Entregado" };

export default function ClientDashboard() {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    API.get("/quotations").then((r) => setQuotes(r.data.slice(0, 5)));
    API.get("/orders").then((r) => setOrders(r.data.slice(0, 5)));
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="client-dashboard">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0047AB] mb-2">Mi Portal</p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>
            Hola, {user?.name}
          </h1>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Link to="/cotizar" className="bg-white border border-[#E2E8F0] rounded-sm p-5 hover:border-[#0047AB] transition-colors group" data-testid="quick-action-quote">
            <FileText className="w-5 h-5 text-[#0047AB] mb-2" />
            <p className="text-sm font-semibold text-[#0F172A]">Nueva Cotización</p>
            <p className="text-xs text-[#475569]">Cotiza un nuevo pedido</p>
          </Link>
          <Link to="/portal/cotizaciones" className="bg-white border border-[#E2E8F0] rounded-sm p-5 hover:border-[#0047AB] transition-colors" data-testid="quick-action-quotes-list">
            <Clock className="w-5 h-5 text-[#F59E0B] mb-2" />
            <p className="text-sm font-semibold text-[#0F172A]">Mis Cotizaciones</p>
            <p className="text-xs text-[#475569]">{quotes.length} cotización(es) recientes</p>
          </Link>
          <Link to="/portal/pedidos" className="bg-white border border-[#E2E8F0] rounded-sm p-5 hover:border-[#0047AB] transition-colors" data-testid="quick-action-orders-list">
            <Package className="w-5 h-5 text-[#10B981] mb-2" />
            <p className="text-sm font-semibold text-[#0F172A]">Mis Pedidos</p>
            <p className="text-xs text-[#475569]">{orders.length} pedido(s) activos</p>
          </Link>
        </div>

        {/* Recent Quotes */}
        <div className="bg-white border border-[#E2E8F0] rounded-sm mb-6">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E8F0]">
            <h2 className="text-sm font-bold text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Cotizaciones Recientes</h2>
            <Link to="/portal/cotizaciones" className="text-xs text-[#0047AB] font-medium flex items-center gap-1 hover:underline">Ver todas <ArrowRight className="w-3 h-3" /></Link>
          </div>
          {quotes.length === 0 ? (
            <p className="px-5 py-8 text-sm text-[#94A3B8] text-center">No tienes cotizaciones aún</p>
          ) : (
            <div className="divide-y divide-[#E2E8F0]">
              {quotes.map((q) => (
                <div key={q.id} className="px-5 py-3 flex items-center justify-between" data-testid={`client-quote-${q.id}`}>
                  <div>
                    <p className="text-sm font-medium text-[#0F172A]">{q.items?.[0]?.product_name || "Cotización"}</p>
                    <p className="text-xs text-[#475569]">{q.items?.[0]?.quantity} piezas · {new Date(q.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`status-${q.status} px-2 py-1 text-xs font-bold uppercase tracking-wider rounded-sm`}>{STATUS_LABELS[q.status] || q.status}</span>
                    <span className="text-sm font-semibold text-[#0F172A]">Q{q.total}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Orders */}
        <div className="bg-white border border-[#E2E8F0] rounded-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E8F0]">
            <h2 className="text-sm font-bold text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Pedidos Activos</h2>
            <Link to="/portal/pedidos" className="text-xs text-[#0047AB] font-medium flex items-center gap-1 hover:underline">Ver todos <ArrowRight className="w-3 h-3" /></Link>
          </div>
          {orders.length === 0 ? (
            <p className="px-5 py-8 text-sm text-[#94A3B8] text-center">No tienes pedidos activos</p>
          ) : (
            <div className="divide-y divide-[#E2E8F0]">
              {orders.map((o) => (
                <Link key={o.id} to={`/portal/pedidos/${o.id}`} className="px-5 py-3 flex items-center justify-between hover:bg-[#F8FAFC] transition-colors block" data-testid={`client-order-${o.id}`}>
                  <div>
                    <p className="text-sm font-medium text-[#0F172A]">{o.items?.[0]?.product_name || "Pedido"}</p>
                    <p className="text-xs text-[#475569]">{new Date(o.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`status-${o.status} px-2 py-1 text-xs font-bold uppercase tracking-wider rounded-sm`}>{ORDER_STATUS[o.status] || o.status}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
